import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { API } from "@/App";
import { toast } from "sonner";
import { 
  Compass, Umbrella, Landmark, Users, Wallet, 
  DollarSign, Crown, Globe, MapPin, Plane,
  ChevronRight, ChevronLeft, Check, Target,
  Calendar, Heart, Sparkles, Mountain, Palmtree,
  Building, Ship, Train, Tent, Camera
} from "lucide-react";

const quizSteps = [
  {
    id: "travel_style",
    question: "What's your travel style?",
    options: [
      { value: "adventure", label: "Adventure", icon: Compass, description: "Hiking, exploring, trying new things" },
      { value: "relaxation", label: "Relaxation", icon: Umbrella, description: "Beach, spa, taking it easy" },
      { value: "culture", label: "Culture", icon: Landmark, description: "Museums, history, local experiences" },
      { value: "family", label: "Family Fun", icon: Users, description: "Kid-friendly, theme parks, activities" }
    ]
  },
  {
    id: "budget_range",
    question: "What's your typical travel budget?",
    options: [
      { value: "budget", label: "Budget Traveler", icon: Wallet, description: "Under $50/day - Hostels, street food" },
      { value: "moderate", label: "Mid-Range", icon: DollarSign, description: "$50-150/day - Nice hotels, restaurants" },
      { value: "luxury", label: "Luxury", icon: Crown, description: "$150+/day - 5-star experiences" }
    ]
  },
  {
    id: "travel_frequency",
    question: "How often do you travel?",
    options: [
      { value: "rarely", label: "Dream Trip Saver", icon: Globe, description: "Saving for that one big trip" },
      { value: "once_year", label: "Annual Explorer", icon: MapPin, description: "One special vacation per year" },
      { value: "multiple_times", label: "Frequent Flyer", icon: Plane, description: "Multiple trips throughout the year" }
    ]
  },
  {
    id: "saving_goal",
    question: "What's your main saving goal?",
    options: [
      { value: "honeymoon", label: "Honeymoon", icon: Heart, description: "Romantic getaway for two" },
      { value: "family_vacation", label: "Family Vacation", icon: Users, description: "Making memories with loved ones" },
      { value: "solo_adventure", label: "Solo Adventure", icon: Compass, description: "Discovering yourself through travel" },
      { value: "bucket_list", label: "Bucket List Trip", icon: Target, description: "That dream destination you've always wanted" }
    ]
  },
  {
    id: "destination_type",
    question: "What type of destinations excite you most?",
    multiSelect: true,
    maxSelect: 3,
    options: [
      { value: "beach", label: "Beach & Islands", icon: Palmtree, description: "Sun, sand, and sea" },
      { value: "mountain", label: "Mountains", icon: Mountain, description: "Peaks and fresh air" },
      { value: "city", label: "Big Cities", icon: Building, description: "Urban exploration" },
      { value: "cruise", label: "Cruises", icon: Ship, description: "Floating adventures" },
      { value: "road_trip", label: "Road Trips", icon: Train, description: "Open road freedom" },
      { value: "camping", label: "Camping & Nature", icon: Tent, description: "Back to basics" }
    ]
  },
  {
    id: "timeline",
    question: "When are you planning your next trip?",
    options: [
      { value: "3_months", label: "Within 3 months", icon: Sparkles, description: "Soon! Let's make it happen" },
      { value: "6_months", label: "4-6 months", icon: Calendar, description: "Planning ahead" },
      { value: "1_year", label: "6-12 months", icon: Target, description: "Building up savings" },
      { value: "no_timeline", label: "Just dreaming", icon: Camera, description: "No rush, enjoying the journey" }
    ]
  }
];

export default function Onboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const user = location.state?.user;
  const progress = ((currentStep + 1) / quizSteps.length) * 100;
  
  const handleSelect = (stepId, value) => {
    const currentQuestion = quizSteps[currentStep];
    
    if (currentQuestion.multiSelect) {
      const currentSelections = answers[stepId] || [];
      if (currentSelections.includes(value)) {
        // Remove if already selected
        setAnswers(prev => ({
          ...prev,
          [stepId]: currentSelections.filter(v => v !== value)
        }));
      } else if (currentSelections.length < (currentQuestion.maxSelect || 3)) {
        // Add if under limit
        setAnswers(prev => ({
          ...prev,
          [stepId]: [...currentSelections, value]
        }));
      }
    } else {
      setAnswers(prev => ({ ...prev, [stepId]: value }));
    }
  };
  
  const handleNext = () => {
    if (currentStep < quizSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleSubmit();
    }
  };
  
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };
  
  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API}/user/onboarding`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          travel_style: answers.travel_style,
          budget_range: answers.budget_range,
          travel_frequency: answers.travel_frequency,
          saving_goal: answers.saving_goal,
          destination_types: answers.destination_type || [],
          trip_timeline: answers.timeline,
          preferred_destinations: answers.destination_type || []
        })
      });
      
      if (!response.ok) throw new Error("Failed to save preferences");
      
      toast.success("Welcome to Savecation! Your journey begins now 🎉");
      navigate("/dashboard");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const currentQuestion = quizSteps[currentStep];
  const isMultiSelect = currentQuestion.multiSelect;
  const currentAnswer = answers[currentQuestion.id];
  const isAnswered = isMultiSelect 
    ? (currentAnswer && currentAnswer.length > 0)
    : !!currentAnswer;
  const isLastStep = currentStep === quizSteps.length - 1;
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white" data-testid="onboarding-page">
      {/* Header */}
      <header className="px-6 py-4">
        <div className="max-w-xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <span className="text-sm text-slate-500">Step {currentStep + 1} of {quizSteps.length}</span>
            <button 
              onClick={() => navigate("/dashboard")}
              className="text-sm text-slate-500 hover:text-slate-700"
            >
              Skip for now
            </button>
          </div>
          <Progress value={progress} className="h-2 rounded-full" />
        </div>
      </header>
      
      {/* Quiz Content */}
      <main className="px-6 py-8">
        <div className="max-w-xl mx-auto">
          {/* Greeting on first step */}
          {currentStep === 0 && user && (
            <div className="text-center mb-8 animate-fade-in-up">
              <p className="text-lg text-slate-600">
                Hey <span className="font-bold text-sky-600">{user.name?.split(' ')[0]}</span>! 👋
              </p>
              <p className="text-slate-500">Let's personalize your experience</p>
            </div>
          )}
          
          {/* Question */}
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 text-center mb-2 animate-fade-in-up">
            {currentQuestion.question.replace(/'/g, "\u2019")}
          </h1>
          
          {/* Multi-select hint */}
          {isMultiSelect && (
            <p className="text-center text-sm text-slate-500 mb-6">
              Select up to {currentQuestion.maxSelect} options
            </p>
          )}
          
          {/* Options */}
          <div className={`mb-8 ${isMultiSelect ? 'grid grid-cols-2 gap-3' : 'space-y-4'}`}>
            {currentQuestion.options.map((option, index) => {
              const isSelected = isMultiSelect 
                ? (currentAnswer || []).includes(option.value)
                : currentAnswer === option.value;
              const Icon = option.icon;
              
              if (isMultiSelect) {
                return (
                  <button
                    key={option.value}
                    onClick={() => handleSelect(currentQuestion.id, option.value)}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      isSelected 
                        ? "border-sky-500 bg-sky-50" 
                        : "border-slate-200 hover:border-slate-300 bg-white"
                    }`}
                    data-testid={`option-${option.value}`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 ${
                      isSelected ? "bg-sky-500 text-white" : "bg-slate-100 text-slate-600"
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <p className="font-semibold text-slate-900 text-sm">{option.label}</p>
                    <p className="text-xs text-slate-500">{option.description}</p>
                    {isSelected && (
                      <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-sky-500 flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </button>
                );
              }
              
              return (
                <button
                  key={option.value}
                  onClick={() => handleSelect(currentQuestion.id, option.value)}
                  className={`quiz-option w-full animate-fade-in-up stagger-${index + 1} ${isSelected ? "selected" : ""}`}
                  data-testid={`option-${option.value}`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    isSelected ? "bg-sky-500 text-white" : "bg-slate-100 text-slate-600"
                  }`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-semibold text-slate-900">{option.label}</p>
                    <p className="text-sm text-slate-500">{option.description}</p>
                  </div>
                  {isSelected && (
                    <div className="w-8 h-8 rounded-full bg-sky-500 flex items-center justify-center">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          
          {/* Navigation */}
          <div className="flex gap-4">
            {currentStep > 0 && (
              <Button 
                onClick={handleBack}
                variant="outline"
                className="flex-1 h-12 rounded-full font-bold"
              >
                <ChevronLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
            )}
            <Button
              onClick={handleNext}
              disabled={!isAnswered || isSubmitting}
              className={`savecation-button ${currentStep === 0 ? "w-full" : "flex-1"}`}
              data-testid="next-btn"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </span>
              ) : isLastStep ? (
                <span className="flex items-center gap-2">
                  Start Saving!
                  <Sparkles className="w-5 h-5" />
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Next
                  <ChevronRight className="w-5 h-5" />
                </span>
              )}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
